import { LockOutlined } from '@ant-design/icons';
import { Button, Form } from 'antd';
import { useForm } from 'react-hook-form';
import { PasswordField } from '@/components/auth';
import { useNotify } from '@/hooks/useNotify';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useMutationReset } from '@/hooks/useMutationReset';
import { useUpdatePasswordMutation } from '@/modules/user/userApi';
import type { ChangePasswordPayload } from '@/types/auth';
import { resolveMutationResult } from '@/utils/apiResponse';
import {
  confirmPasswordRules,
  PASSWORD_REUSED_MESSAGE,
  passwordFieldRules,
  requiredPasswordRule,
} from '@/utils/validation';
import styles from '../Account.module.scss';

export function PasswordTabContent() {
  const notify = useNotify();
  const [updatePassword, { isLoading, reset }] = useUpdatePasswordMutation();
  useGlobalLoader(isLoading);
  const { control, handleSubmit, watch, reset: resetForm } = useForm<ChangePasswordPayload>({
    defaultValues: {
      currentPassword: '',
      password: '',
      confirmPassword: '',
    },
  });

  useMutationReset(reset);

  const onSubmit = async (values: ChangePasswordPayload) => {
    const result = await resolveMutationResult(updatePassword(values));

    if (result.ok) {
      notify.success(result.message ?? 'Password updated successfully.');
      resetForm();
      return;
    }

    notify.error(result.message);
  };

  return (
    <div className={styles.tabContent}>
      <div className={styles.passwordHint}>
        <LockOutlined />
        <span>
          Use at least 8 characters with uppercase, lowercase, number, and special character.{' '}
          {PASSWORD_REUSED_MESSAGE}
        </span>
      </div>

      <Form
        layout="vertical"
        className={`${styles.profileForm} ${styles.passwordForm}`}
        onFinish={handleSubmit(onSubmit)}
      >
        <PasswordField
          control={control}
          name="currentPassword"
          label="Current password"
          rules={requiredPasswordRule}
          size="middle"
        />

        <div className={styles.passwordFormGrid}>
          <PasswordField
            control={control}
            name="password"
            label="New password"
            rules={passwordFieldRules}
            size="middle"
          />

          <PasswordField
            control={control}
            name="confirmPassword"
            label="Confirm password"
            rules={confirmPasswordRules(() => watch('password'))}
            size="middle"
          />
        </div>

        <div className={styles.formActions}>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            Update password
          </Button>
        </div>
      </Form>
    </div>
  );
}
